"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Asset } from "./types";
import { useOspApiClientProvider } from "@/src/hooks";

const DEFAULT_STOCK_IDS = ["AAPL", "NVDA", "TSLA", "GOOG"] as const;
const DEFAULT_CRYPTO_IDS = ["BTC", "ETH", "SOL", "BNB"] as const;

const formatPrice = (value?: number | string): string => {
  const num = Number(value);
  if (Number.isFinite(num)) {
    return `$${num.toLocaleString("en-US", {
      maximumFractionDigits: num >= 100 ? 0 : 2,
    })}`;
  }
  return "--";
};

const formatChange = (value?: number | string): string => {
  const num = Number(value);
  if (Number.isFinite(num)) {
    return `${num >= 0 ? "+" : ""}${num.toFixed(2)}%`;
  }
  return "--";
};

const buildCryptoAssets = (rows: any[] = []): Asset[] => {
  const tokensMap = new Map<string, any>();
  rows.forEach((row) => {
    const key = row?.symbol?.toUpperCase();
    if (key) {
      tokensMap.set(key, row);
    }
  });

  return DEFAULT_CRYPTO_IDS.map((symbol) => {
    const tokenInfo = tokensMap.get(symbol.toUpperCase());
    if (!tokenInfo) {
      return null;
    }
    const marketData = tokenInfo?.market_data;
    const changeEntry = marketData?.price_change_percentage?.find(
      (item: any) => item?.time_period === "24h",
    );
    return {
      id: tokenInfo?.id ?? symbol,
      ticker: tokenInfo?.symbol ?? symbol,
      name: tokenInfo?.name ?? symbol,
      iconUrl: tokenInfo?.logo ?? "",
      value: formatPrice(marketData?.current_price),
      change: formatChange(changeEntry?.percentage),
      isSelected: true,
    };
  }).filter(Boolean) as Asset[];
};

const buildStockAssets = (rows: any[] = []): Asset[] => {
  const stocksMap = new Map<string, any>();
  rows.forEach((row) => {
    const key = row?.symbol?.toUpperCase() || row?.ticker?.toUpperCase();
    if (key) {
      stocksMap.set(key, row);
    }
  });

  return DEFAULT_STOCK_IDS.map((symbol) => {
    const record = stocksMap.get(symbol.toUpperCase());
    if (!record) {
      return null;
    }
    const price = record?.trading_data?.current_price;
    const previousClose = record?.trading_data?.previous_close;
    const change =
      Number(price) && Number(previousClose)
        ? (Number(price) / Number(previousClose) - 1) * 100
        : undefined;
    return {
      id: record?.id ?? symbol,
      ticker: record?.symbol ?? symbol,
      name: record?.company_name ?? symbol,
      iconUrl: record?.logo ?? "",
      value: formatPrice(price),
      change: formatChange(change),
      isSelected: true,
    };
  }).filter(Boolean) as Asset[];
};

export const useRecommendAssets = () => {
  const { openDataApi } = useOspApiClientProvider();

  const cryptoQuery = useQuery({
    queryKey: ["recommend-assets", "crypto", DEFAULT_CRYPTO_IDS],
    enabled: !!openDataApi,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await openDataApi?.tokens.getTokens({
        limit: DEFAULT_CRYPTO_IDS.length,
        symbols: [...DEFAULT_CRYPTO_IDS],
        offset: 0,
        "enrich.enable": true,
        "enrich.with_market.enable": true,
      });
      return res?.data?.data?.rows ?? [];
    },
    select: (rows) => buildCryptoAssets(rows),
  });

  const stocksQuery = useQuery({
    queryKey: ["recommend-assets", "stocks", DEFAULT_STOCK_IDS],
    enabled: !!openDataApi,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await openDataApi?.stocks.getStocks({
        symbols: [...DEFAULT_STOCK_IDS],
        limit: DEFAULT_STOCK_IDS.length,
        "enrich.with_trading_data": true,
        "enrich.enable": true,
      } as any);
      return res?.data?.data?.rows ?? [];
    },
    select: (rows) => buildStockAssets(rows),
  });

  const cryptoAssets = useMemo(
    () => cryptoQuery.data ?? [],
    [cryptoQuery.data],
  );
  const stockAssets = useMemo(() => stocksQuery.data ?? [], [stocksQuery.data]);

  return {
    crypto: cryptoAssets,
    stocks: stockAssets,
    isLoading: cryptoQuery.isLoading || stocksQuery.isLoading,
  };
};
